import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, Sparkle, WarningOctagon, PaperPlaneTilt, Clipboard } from "@phosphor-icons/react";

/**
 * Paste in a PMR ETA email (AppFolio, Buildium, whatever) → Claude parses →
 * admin confirms → job created with is_eta_request=true, priority=urgent.
 * Shows in the red banner on Dashboard until assigned.
 */
export default function EtaRequestModal({ open, onClose, onCreated }) {
  const [step, setStep] = useState("paste"); // paste | confirm
  const [raw, setRaw] = useState("");
  const [parsing, setParsing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [parsed, setParsed] = useState(null);

  const reset = () => {
    setStep("paste"); setRaw(""); setParsed(null); setParsing(false); setCreating(false);
  };
  const close = () => { reset(); onClose?.(); };

  const parse = async () => {
    if (!raw.trim() || raw.trim().length < 10) {
      toast.error("Paste in the ETA email first");
      return;
    }
    setParsing(true);
    try {
      const { data } = await api.post("/eta-requests/parse", { raw_text: raw });
      setParsed(data);
      setStep("confirm");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI parse failed");
    } finally {
      setParsing(false);
    }
  };

  const setField = (k, v) => setParsed(prev => ({ ...prev, [k]: v }));

  const create = async () => {
    if (!parsed?.property_address?.trim()) {
      toast.error("Address is required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/eta-requests", {
        ...parsed,
        raw_email_text: raw,
      });
      toast.success("ETA request created — heading to the dispatch board.");
      onCreated?.(data);
      close();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-start sm:items-center justify-center p-4"
      onClick={close} data-testid="eta-request-modal">
      <div className="bg-white border-2 border-[#0f172a] shadow-[10px_10px_0px_#dc2626] w-full max-w-2xl max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0f172a] bg-red-50">
          <div className="flex items-center gap-2">
            <WarningOctagon weight="fill" size={22} className="text-red-600"/>
            <div>
              <div className="font-display font-black uppercase tracking-tight text-lg">Log ETA Request</div>
              <div className="text-[11px] font-bold text-slate-600">Paste the email → AI extracts details → drops into dispatch as urgent.</div>
            </div>
          </div>
          <button onClick={close} aria-label="Close" data-testid="eta-modal-close"
            className="p-2 hover:bg-red-100"><X weight="bold" size={16}/></button>
        </div>

        {step === "paste" && (
          <div className="p-4 space-y-3 flex-1 overflow-auto">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
              Paste the full email (subject + body) or just the body — Claude reads either.
            </div>
            <textarea
              value={raw}
              onChange={e => setRaw(e.target.value)}
              data-testid="eta-raw-textarea"
              rows={12}
              placeholder={"Subject: ETA: Work Order #3330 for 886 N 49th St - 1 from Property Management Redefined\n\nTenant: Lauren Long (267) 251-7052 llong9112@gmail.com\nIssue: Landscaping — Trees\nDescription: Tenant reports a thick stump/stub forming at the base of the home…"}
              className="w-full p-3 border-2 border-[#0f172a] text-sm font-mono resize-y focus:outline-none"
            />
            <div className="flex items-center gap-2 justify-end">
              <button onClick={close} data-testid="eta-cancel"
                className="h-11 px-4 border-2 border-slate-300 text-slate-600 text-[11px] font-black uppercase tracking-widest hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={parse} disabled={parsing || raw.length < 10} data-testid="eta-parse-btn"
                className="h-11 px-5 bg-[#0f172a] text-white text-[11px] font-black uppercase tracking-widest hover:bg-black disabled:opacity-40 inline-flex items-center gap-2">
                {parsing ? "AI reading…" : <><Sparkle weight="fill" size={12}/> Parse With AI</>}
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && parsed && (
          <div className="p-4 space-y-3 flex-1 overflow-auto">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Clipboard weight="bold" size={12}/> Confirm the extracted fields — edit anything AI got wrong.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Work Order #" v={parsed.work_order_number} onChange={v => setField("work_order_number", v)} testId="eta-wo"/>
              <Field label="Urgency" v={parsed.urgency_hint} onChange={v => setField("urgency_hint", v)} testId="eta-urgency"
                options={["low", "normal", "urgent", "emergency"]}/>
              <Field label="Property Address *" v={parsed.property_address} onChange={v => setField("property_address", v)} testId="eta-addr" span/>
              <Field label="Unit" v={parsed.unit} onChange={v => setField("unit", v)} testId="eta-unit"/>
              <Field label="Tenant Name" v={parsed.tenant_name} onChange={v => setField("tenant_name", v)} testId="eta-tenant"/>
              <Field label="Tenant Phone" v={parsed.tenant_phone} onChange={v => setField("tenant_phone", v)} testId="eta-tenant-phone"/>
              <Field label="Tenant Email" v={parsed.tenant_email} onChange={v => setField("tenant_email", v)} testId="eta-tenant-email"/>
              <Field label="Issue" v={parsed.issue} onChange={v => setField("issue", v)} testId="eta-issue" span/>
              <Field label="Description" v={parsed.description} onChange={v => setField("description", v)} testId="eta-desc" span multiline/>
              <Field label="Sending PMR" v={parsed.pmr_name} onChange={v => setField("pmr_name", v)} testId="eta-pmr"/>
              <Field label="PMR Reply-To" v={parsed.pmr_email} onChange={v => setField("pmr_email", v)} testId="eta-pmr-email"/>
            </div>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600 pt-1">
              <input type="checkbox" checked={parsed.has_permission_to_enter} onChange={e => setField("has_permission_to_enter", e.target.checked)}
                data-testid="eta-permission"
                className="h-4 w-4 accent-[#00C4CC]"/>
              Tenant has given permission to enter
            </label>
            <div className="flex items-center gap-2 justify-end pt-2">
              <button onClick={() => setStep("paste")} data-testid="eta-back"
                className="h-11 px-4 border-2 border-slate-300 text-slate-600 text-[11px] font-black uppercase tracking-widest hover:bg-slate-50">
                Back
              </button>
              <button onClick={create} disabled={creating} data-testid="eta-create-btn"
                className="h-11 px-5 bg-red-600 hover:bg-red-700 text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-2">
                {creating ? "Creating…" : <><PaperPlaneTilt weight="fill" size={12}/> Create ETA Request</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, v, onChange, testId, span, multiline, options }) {
  const cls = "w-full px-3 py-2 border-2 border-slate-300 text-sm font-medium focus:border-[#0f172a] focus:outline-none";
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      {options ? (
        <select value={v || ""} onChange={e => onChange(e.target.value)} data-testid={testId} className={cls + " h-10"}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : multiline ? (
        <textarea value={v || ""} onChange={e => onChange(e.target.value)} rows={3} data-testid={testId} className={cls + " resize-y"}/>
      ) : (
        <input type="text" value={v || ""} onChange={e => onChange(e.target.value)} data-testid={testId} className={cls + " h-10"}/>
      )}
    </div>
  );
}
